import type { PersistedSliceDescriptor, ProductionStorageBackendKind } from "../types";
import { authSlice } from "./auth.slice";
import { bikePrefsSlice } from "./bikePrefs.slice";
import { carPrefsSlice } from "./carPrefs.slice";
import { fabUiSlice } from "./fabUi.slice";
import { lastGpsDataSlice } from "./lastGpsData.slice";
import { mapPrefsSlice } from "./mapPrefs.slice";
import { pricingPrefsSlice } from "./pricingPrefs.slice";
import { purchasedItinerariesSlice } from "./purchasedItineraries.slice";
import { rentalVehiclesPrefsSlice } from "./rentalVehiclesPrefs.slice";
import { savedItinerariesSlice } from "./savedItineraries.slice";
import { searchHistorySlice } from "./searchHistory.slice";
import { transitPrefsSlice } from "./transitPrefs.slice";

/** Backend kinds allowed on slices shipped to production (`ALL_PERSISTED_SLICES`). */
const PRODUCTION_STORAGE_KINDS: readonly ProductionStorageBackendKind[] = [
    "preferences",
    "secure",
    "idb",
];

/**
 * Fails fast when a test-only backend is registered for a production slice.
 *
 * @param slices Slice list to validate (typically `ALL_PERSISTED_SLICES`).
 * @throws When any slice uses `inMemoryTest` or an unknown kind.
 */
const assertProductionStorageBackends = ( slices: readonly PersistedSliceDescriptor<unknown>[] ): void => {
    for ( const slice of slices ) {
        if ( !PRODUCTION_STORAGE_KINDS.includes( slice.storage as ProductionStorageBackendKind ) ) {
            throw new Error( `Test-only storage backend "${ slice.storage }" is not allowed in production registry (slice "${ slice.key }")` );
        }
    }
};

/**
 * Slices hydrated at application boot inside `PersistenceProvider`.
 *
 * @remarks Order is not semantically significant; hydration runs in parallel.
 */
export const EAGER_PERSISTED_SLICES = [
    bikePrefsSlice,
    carPrefsSlice,
    mapPrefsSlice,
    pricingPrefsSlice,
    rentalVehiclesPrefsSlice,
    lastGpsDataSlice,
    authSlice,
    transitPrefsSlice,
] as const satisfies readonly PersistedSliceDescriptor<unknown>[];

/**
 * Complete registry of persisted slices (eager + lazy).
 *
 * @remarks Lazy slices (`fabUi`, `searchHistory`, `savedItineraries`, `purchasedItineraries`) load on first hook subscription.
 */
export const ALL_PERSISTED_SLICES = [
    ...EAGER_PERSISTED_SLICES,
    fabUiSlice,
    searchHistorySlice,
    savedItinerariesSlice,
    purchasedItinerariesSlice,
] as const satisfies readonly PersistedSliceDescriptor<unknown>[];

assertProductionStorageBackends( ALL_PERSISTED_SLICES );
